import { useEffect, useId, useRef, useState, type FormEvent } from "react";
import type { CloseNightlyStatus } from "@gioia365/contracts";
import { useQuery } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import { useTab } from "../api/queries";
import { Button } from "../design/components/Button";
import { Checkbox } from "../design/components/Checkbox";
import { Reason } from "../design/components/Reason";
import { TextField } from "../design/components/TextField";
import { useToast } from "../design/components/Toast";
import { messageForError } from "../i18n/errors";
import { useT } from "../i18n/locale";
import { useAccess, useMenuSubject } from "../session/access";
import { switchTab } from "../session/switch-tab";
import { whenParts } from "./close-import-format";
import { closeNightlyQuery, useCloseNightlyActions } from "./close-import-queries";

// «Ogni sera da Close» (INGR-12, confermata da Lucio il 18/10/2026), in cima a «Importazione guidata da Close». Il Super admin di
// Gioia365 (`canManage`, il contesto di piattaforma) salva o toglie la chiave API di Close (un campo password: la chiave non torna mai
// dal server, la pagina dice soltanto quando e chi l'ha salvata), fa la prova senza salvare, «Importa adesso» e accende «Importa ogni sera
// alle 21:00»; importazione e interruttore aspettano una prova senza salvare finita con la chiave salvata. Gli altri che vedono la pagina
// leggono soltanto se è accesa, la prossima importazione e l'ultima della sera; se chi guarda è il Super admin di Gioia365 entrato
// come persona del team, al posto di «La accende e la spegne…» c'è «Entra come Super admin di Gioia365», che passa al contesto di
// piattaforma della stessa azienda e torna su questa pagina (Lucio, 18/10/2026). Le date nel fuso dell'azienda, come il resto della
// pagina; le 21:00 sono l'ora italiana.

type Props = {
  /** Dopo un avvio: la pagina apre l'esecuzione appena partita. */
  onStarted: (runId: string) => void;
};

export function CloseNightlySection({ onStarted }: Props) {
  const t = useT();
  const tab = useTab();
  const titleId = useId();
  const status = useQuery(closeNightlyQuery(tab));
  return (
    <section className="g-ci__set g-ci__nightly" aria-labelledby={titleId}>
      <div className="g-ci__set-title">
        <h2 id={titleId}>{t("closeImport.nightly.title")}</h2>
        <p className="g-ci__muted">{t("closeImport.nightly.intro")}</p>
      </div>
      {status.data ? (
        status.data.canManage ? (
          <Manage data={status.data} onStarted={onStarted} />
        ) : (
          <ReadOnly data={status.data} />
        )
      ) : status.isError ? (
        <div className="g-ci__problem">
          <Reason tone="warning" role="alert">
            {messageForError(t, status.error)}
          </Reason>
          <Button onClick={() => void status.refetch()}>{t("common.retry")}</Button>
        </div>
      ) : (
        <p className="g-ci__muted">{t("common.loading")}</p>
      )}
    </section>
  );
}

const NEXT = { soon: "closeImport.nightly.nextSoon", today: "closeImport.nightly.nextToday", tomorrow: "closeImport.nightly.nextTomorrow" } as const;

/**
 * «Prossima importazione: oggi alle 21:00» (o «stasera, entro pochi minuti», «domani alle 21:00»), oppure «L'importazione di ogni sera è
 * spenta»; accesa ma senza una prova senza salvare finita con la chiave di adesso, il motivo per cui non parte al posto dell'ora.
 */
function NextLine({ data }: { data: CloseNightlyStatus }) {
  const t = useT();
  const { next } = data.nightly;
  if (!data.nightly.enabled || !next) return <p>{t("closeImport.nightly.off")}</p>;
  if (!data.ready) return <Reason tone="warning">{t("closeImport.nightly.notReady")}</Reason>;
  return <p>{t(NEXT[next.day])}</p>;
}

/** I motivi che si dicono in parole semplici; gli altri codici: «lo controlla il team», con il codice in piccolo. */
// `close_unavailable` è anche l'errore di rete e il 5xx di Close dopo i tentativi: la sera dopo riprova da sola.
const REASONS = new Set(["close_auth_failed", "close_key_unreadable", "close_dry_run_required", "close_unavailable"] as const);
type KnownReason = typeof REASONS extends Set<infer T> ? T : never;
const isKnownReason = (code: string): code is KnownReason => (REASONS as Set<string>).has(code);

/** «Ultima importazione della sera: 18/10/2026 alle 21:00, finita.», e se si è fermata con un errore il perché. */
function LastLine({ data }: { data: CloseNightlyStatus }) {
  const t = useT();
  const access = useAccess();
  const last = data.nightly.last;
  if (!last) return null;
  const { date, time } = last.startedAt ? whenParts(access.context.settings.timeZone, last.startedAt) : { date: last.day.split("-").reverse().join("/"), time: "21:00" };
  const error = last.outcome === "failed" ? last.error : null;
  return (
    <>
      <p className="g-ci__muted">{t("closeImport.nightly.last", { date, time, outcome: t(`closeImport.nightly.outcome.${last.outcome}`) })}</p>
      {error === null ? null : isKnownReason(error) ? (
        <Reason tone="warning">{t(`closeImport.nightly.reason.${error}`)}</Reason>
      ) : (
        <Reason tone="warning">
          <span>{t("closeImport.nightly.reason.other")}</span> <small>{t("closeImport.nightly.reasonCode", { code: error })}</small>
        </Reason>
      )}
    </>
  );
}

function ReadOnly({ data }: { data: CloseNightlyStatus }) {
  const t = useT();
  return (
    <div className="g-ci__nightly-part">
      <p>
        <strong>{t(data.nightly.enabled ? "closeImport.nightly.readOnlyOn" : "closeImport.nightly.readOnlyOff")}</strong>
      </p>
      {data.nightly.enabled ? <NextLine data={data} /> : null}
      <LastLine data={data} />
      <WhoManages />
    </div>
  );
}

/**
 * Chi la accende e la spegne. Al Super admin di Gioia365 che guarda come persona del team (il menu della scheda è il suo, non quello
 * della «Guarda come…») la scorciatoia: lo stesso cambio di contesto di «Entra» in «Aziende di Gioia365», per questa azienda, che
 * riapre questa pagina invece della Home. Il server resta l'autorità: nel contesto di piattaforma risponde `canManage`.
 */
function WhoManages() {
  const t = useT();
  const access = useAccess();
  const { platformAdmin } = useMenuSubject();
  const router = useRouter();
  const [entering, setEntering] = useState(false);
  if (!platformAdmin || access.isPlatform) return <p className="g-ci__muted">{t("closeImport.nightly.readOnlyWho")}</p>;
  const enter = async () => {
    if (entering) return;
    setEntering(true);
    try {
      await switchTab(router, { kind: "platform", organizationId: access.context.organization.id }, router.state.location.href);
    } finally {
      setEntering(false);
    }
  };
  return (
    <>
      <p>{t("closeImport.nightly.platformShortcut")}</p>
      <div className="g-ci__nightly-actions">
        <Button onClick={() => void enter()} disabled={entering}>
          {t("closeImport.nightly.platformShortcutButton")}
        </Button>
      </div>
    </>
  );
}

function Manage({ data, onStarted }: { data: CloseNightlyStatus; onStarted: (runId: string) => void }) {
  const t = useT();
  const tab = useTab();
  const toast = useToast();
  const access = useAccess();
  const timeZone = access.context.settings.timeZone;
  const actions = useCloseNightlyActions(tab);
  const keyId = useId();
  const switchId = useId();
  const [draft, setDraft] = useState("");
  const [keyError, setKeyError] = useState<string | undefined>(undefined);
  // La conferma vale per la chiave che c'era quando è stata chiesta: una chiave che cambia (salvata o tolta altrove) la chiude.
  const savedAt = data.key?.savedAt ?? null;
  const [confirmFor, setConfirmFor] = useState<string | null>(null);
  const confirmRemoval = savedAt !== null && confirmFor === savedAt;
  const setConfirmRemoval = (on: boolean) => setConfirmFor(on ? savedAt : null);
  const confirmButton = useRef<HTMLButtonElement>(null);
  // La conferma porta il fuoco sul «Sì».
  useEffect(() => {
    if (confirmRemoval) confirmButton.current?.focus();
  }, [confirmRemoval]);
  const busy = data.busy || actions.start.isPending;

  const saveKey = (event: FormEvent) => {
    event.preventDefault();
    if (draft.trim() === "" || actions.saveKey.isPending) return;
    const wasOn = data.nightly.enabled;
    setKeyError(undefined);
    actions.saveKey.mutate(draft.trim(), {
      onSuccess: () => {
        setDraft("");
        toast.show(t(wasOn ? "closeImport.nightly.keySavedOffToast" : "closeImport.nightly.keySavedToast"), "success");
      },
      onError: (error) => setKeyError(messageForError(t, error)),
    });
  };
  const removeKey = () =>
    actions.removeKey.mutate(undefined, {
      onSuccess: () => {
        setConfirmRemoval(false);
        toast.show(t("closeImport.nightly.keyRemovedToast"), "success");
      },
      onError: (error) => toast.show(messageForError(t, error), "warning"),
    });
  const start = (what: "dry-run" | "import") =>
    actions.start.mutate(what, {
      onSuccess: (started) => {
        toast.show(t(what === "dry-run" ? "closeImport.nightly.dryRunToast" : "closeImport.nightly.importToast"), "success");
        onStarted(started.runId);
      },
      onError: (error) => toast.show(messageForError(t, error), "warning"),
    });
  const schedule = (enabled: boolean) =>
    actions.schedule.mutate(enabled, {
      onSuccess: () => toast.show(t(enabled ? "closeImport.nightly.onToast" : "closeImport.nightly.offToast"), "success"),
      onError: (error) => toast.show(messageForError(t, error), "warning"),
    });

  const savedKey = data.key ? whenParts(timeZone, data.key.savedAt) : null;
  // L'ultima prova senza salvare con questa chiave: in corso soltanto se è viva (`busy`); «in corso» senza segnale vuol dire fermata. Sotto,
  // l'ultima finita con questa chiave, che regge importazione e interruttore anche se una prova dopo si è fermata.
  const dryRunLines: string[] = [];
  if (data.key) {
    const latest = data.dryRun?.currentKey ? data.dryRun : null;
    const running = latest?.status === "running" && data.busy;
    const stopped = latest?.status === "failed" || latest?.status === "aborted" || (latest?.status === "running" && !data.busy);
    if (running) dryRunLines.push(t("closeImport.nightly.dryRunRunning"));
    else if (stopped) dryRunLines.push(t("closeImport.nightly.dryRunStopped"));
    if (data.dryRunCompletedAt) dryRunLines.push(t("closeImport.nightly.dryRunDone", whenParts(timeZone, data.dryRunCompletedAt)));
    else if (!running && !stopped) dryRunLines.push(t("closeImport.nightly.dryRunMissing"));
  }

  return (
    <>
      <div className="g-ci__nightly-part">
        <h3>{t("closeImport.nightly.keyTitle")}</h3>
        <p>
          {data.key && savedKey
            ? data.key.savedBy
              ? t("closeImport.nightly.keySaved", { date: savedKey.date, time: savedKey.time, person: data.key.savedBy })
              : t("closeImport.nightly.keySavedBySomeone", { date: savedKey.date, time: savedKey.time })
            : t("closeImport.nightly.keyNone")}
        </p>
        <form className="g-ci__nightly-key" onSubmit={saveKey} noValidate>
          <TextField
            id={keyId}
            type="password"
            autoComplete="new-password"
            spellCheck={false}
            label={t("closeImport.nightly.keyLabel")}
            hint={t("closeImport.nightly.keyHint")}
            value={draft}
            onChange={(event) => {
              setDraft(event.target.value);
              setKeyError(undefined);
            }}
            error={keyError}
          />
          <div className="g-ci__nightly-actions">
            <Button type="submit" variant="primary" disabled={draft.trim() === "" || actions.saveKey.isPending}>
              {t(actions.saveKey.isPending ? "closeImport.nightly.keySaving" : "closeImport.nightly.keySave")}
            </Button>
            {data.key && !confirmRemoval ? <Button onClick={() => setConfirmRemoval(true)}>{t("closeImport.nightly.keyRemove")}</Button> : null}
          </div>
        </form>
        {data.key && confirmRemoval ? (
          <div className="g-ci__nightly-confirm" role="group" aria-label={t("closeImport.nightly.keyRemove")}>
            <p>{t("closeImport.nightly.keyRemoveConfirm")}</p>
            <div className="g-ci__nightly-actions">
              <Button ref={confirmButton} variant="primary" onClick={removeKey} disabled={actions.removeKey.isPending}>
                {t("closeImport.nightly.keyRemoveYes")}
              </Button>
              <Button onClick={() => setConfirmRemoval(false)}>{t("common.cancel")}</Button>
            </div>
          </div>
        ) : null}
      </div>

      <div className="g-ci__nightly-part">
        <h3>{t("closeImport.nightly.dryRunTitle")}</h3>
        <p className="g-ci__muted">{t("closeImport.nightly.dryRunText")}</p>
        {dryRunLines.map((line) => (
          <p key={line}>{line}</p>
        ))}
        <div className="g-ci__nightly-actions">
          <Button onClick={() => start("dry-run")} disabled={!data.key || busy}>
            {t("closeImport.nightly.dryRunStart")}
          </Button>
        </div>
      </div>

      <div className="g-ci__nightly-part">
        <h3>{t("closeImport.nightly.eveningTitle")}</h3>
        <Checkbox
          id={switchId}
          label={t("closeImport.nightly.switchLabel")}
          checked={data.nightly.enabled}
          onChange={schedule}
          disabled={actions.schedule.isPending || (!data.nightly.enabled && !data.ready)}
          {...(!data.nightly.enabled && !data.ready ? { hint: t("closeImport.nightly.switchHint") } : {})}
        />
        <NextLine data={data} />
        <LastLine data={data} />
        <p className="g-ci__muted">{t("closeImport.nightly.importNowText")}</p>
        <div className="g-ci__nightly-actions">
          <Button onClick={() => start("import")} disabled={!data.ready || busy}>
            {t("closeImport.nightly.importNow")}
          </Button>
        </div>
        {data.busy ? <Reason>{t("closeImport.nightly.busy")}</Reason> : null}
      </div>
    </>
  );
}
